import { isExistingIsoDay } from '@prospero/shared';
import {
  CONSULTANT_DEADLINE_BUCKETS,
  CONSULTANT_DEADLINE_DOMAINS,
  type ConsultantDeadlineBucket,
  type ConsultantDeadlineDomain,
} from '@shared/dto/deadline/consultant-deadline.dto';
import {
  DEADLINE_RECURRENCE_FILTERS,
  type DeadlineRecurrenceFilter,
} from '@shared/dto/deadline/deadline-management.constants';
import type { ConsultantDeadlineRowFilters } from '../../../hooks/consultant/use-consultant-deadlines';

export type ConsultantDeadlinesView = 'list' | 'agenda' | 'calendar';
export type ConsultantDeadlinesCalendarView = 'month' | 'week';

export interface ConsultantDeadlinesUrlFilters {
  view: ConsultantDeadlinesView;
  search: string;
  tenantId: string;
  /** Persona (id utente): vale solo con un cliente selezionato, query param `person`. */
  assigneeUserId: string;
  domain: ConsultantDeadlineDomain | '';
  bucket: ConsultantDeadlineBucket | '';
  /** Spec §11.1: "Si rinnovano" / "Uniche"; il link del digest apre `bucket=EXPIRED&recurrence=ONE_OFF`. */
  recurrence: DeadlineRecurrenceFilter | '';
  documentTypeId: string;
  /** Testo libero su titolo e tipo documento, query param `document`. */
  document: string;
  dueFrom: string;
  dueTo: string;
  /** Calendar anchor `yyyy-MM`; empty means the current month. */
  month: string;
  /** Calendar granularity, query param `calendarView`: empty means "not chosen" (week on phone, month on desktop). */
  calendarView: ConsultantDeadlinesCalendarView | '';
  /** Week anchor `yyyy-MM-dd`, query param `day`: empty means the current week. */
  day: string;
  page: number;
  /** Vista delle archiviate e chiuse (query param `archived=1`): solo nella lista. */
  archived: boolean;
}

export const DEFAULT_CONSULTANT_DEADLINES_FILTERS: ConsultantDeadlinesUrlFilters = {
  view: 'list',
  search: '',
  tenantId: '',
  assigneeUserId: '',
  domain: '',
  bucket: '',
  recurrence: '',
  documentTypeId: '',
  document: '',
  dueFrom: '',
  dueTo: '',
  month: '',
  calendarView: '',
  day: '',
  page: 1,
  archived: false,
};

/** Reset keeps the view and the calendar month: only the narrowing is cleared. */
export const CLEARED_FILTERS_PATCH: Partial<ConsultantDeadlinesUrlFilters> = {
  search: '',
  tenantId: '',
  assigneeUserId: '',
  domain: '',
  bucket: '',
  recurrence: '',
  documentTypeId: '',
  document: '',
  dueFrom: '',
  dueTo: '',
  archived: false,
};

const ISO_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function isDomain(value: string): value is ConsultantDeadlineDomain {
  return (CONSULTANT_DEADLINE_DOMAINS as readonly string[]).includes(value);
}

function isBucket(value: string): value is ConsultantDeadlineBucket {
  return (CONSULTANT_DEADLINE_BUCKETS as readonly string[]).includes(value);
}

function isRecurrenceFilter(value: string): value is DeadlineRecurrenceFilter {
  return (DEADLINE_RECURRENCE_FILTERS as readonly string[]).includes(value);
}

/** Solo giorni che esistono: `2026-13-45` non deve scivolare su febbraio 2027. */
function validDay(value: string | null): string {
  return isExistingIsoDay(value) ? value : '';
}

function oneOf<Value extends string>(
  raw: string | null,
  isValid: (value: string) => value is Value,
): Value | '' {
  return raw !== null && isValid(raw) ? raw : '';
}

function validMonth(value: string | null): string {
  return value !== null && ISO_MONTH.test(value) ? value : '';
}

function validPage(value: string | null): number {
  const page = Number.parseInt(value ?? '', 10);
  return Number.isFinite(page) && page >= 1 ? page : 1;
}

function parseView(value: string | null): ConsultantDeadlinesView {
  return value === 'calendar' || value === 'agenda' ? value : 'list';
}

function parseCalendarView(value: string | null): ConsultantDeadlinesCalendarView | '' {
  return value === 'week' || value === 'month' ? value : '';
}

/** The URL is the single source of truth: a reload or a shared link lands on the same view. */
export function parseConsultantDeadlinesFilters(
  searchParams: URLSearchParams,
): ConsultantDeadlinesUrlFilters {
  const tenantId = searchParams.get('tenantId') ?? '';
  const view = parseView(searchParams.get('view'));
  return {
    view,
    archived: view === 'list' && searchParams.get('archived') === '1',
    search: searchParams.get('q') ?? '',
    tenantId,
    assigneeUserId: tenantId ? (searchParams.get('person') ?? '') : '',
    domain: oneOf(searchParams.get('domain'), isDomain),
    bucket: oneOf(searchParams.get('bucket'), isBucket),
    recurrence: oneOf(searchParams.get('recurrence'), isRecurrenceFilter),
    documentTypeId: searchParams.get('documentTypeId') ?? '',
    document: searchParams.get('document') ?? '',
    dueFrom: validDay(searchParams.get('dueFrom')),
    dueTo: validDay(searchParams.get('dueTo')),
    month: validMonth(searchParams.get('month')),
    calendarView: parseCalendarView(searchParams.get('calendarView')),
    day: validDay(searchParams.get('day')),
    page: validPage(searchParams.get('page')),
  };
}

export function serializeConsultantDeadlinesFilters(
  filters: ConsultantDeadlinesUrlFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  const entries: Array<[string, string]> = [
    ['view', filters.view === 'list' ? '' : filters.view],
    ['q', filters.search],
    ['tenantId', filters.tenantId],
    ['person', filters.assigneeUserId],
    ['domain', filters.domain],
    ['bucket', filters.bucket],
    ['recurrence', filters.recurrence],
    ['documentTypeId', filters.documentTypeId],
    ['document', filters.document],
    ['dueFrom', filters.dueFrom],
    ['dueTo', filters.dueTo],
    ['month', filters.month],
    ['calendarView', filters.view === 'calendar' ? filters.calendarView : ''],
    ['day', filters.view === 'calendar' && filters.calendarView !== 'month' ? filters.day : ''],
    ['page', filters.page > 1 ? String(filters.page) : ''],
    ['archived', filters.archived && filters.view === 'list' ? '1' : ''],
  ];
  for (const [key, value] of entries) {
    if (value) {
      params.set(key, value);
    }
  }
  return params;
}

export function toRowFilters(filters: ConsultantDeadlinesUrlFilters): ConsultantDeadlineRowFilters {
  return {
    domain: filters.domain,
    bucket: filters.bucket,
    recurrence: filters.recurrence,
    documentTypeId: filters.documentTypeId,
    document: filters.document,
    dueFrom: filters.dueFrom,
    dueTo: filters.dueTo,
    assigneeUserId: filters.assigneeUserId,
    archived: filters.archived,
  };
}

/** The one list of row-filter fields: counting, "is any active" and the view identity all read it. */
function activeRowFilterValues(rowFilters: ConsultantDeadlineRowFilters): string[] {
  return [
    rowFilters.assigneeUserId,
    rowFilters.domain,
    rowFilters.bucket,
    rowFilters.recurrence,
    rowFilters.documentTypeId,
    rowFilters.document,
    rowFilters.dueFrom,
    rowFilters.dueTo,
    rowFilters.archived ? 'archived' : '',
  ].filter((value) => value !== '');
}

export function hasRowFilters(rowFilters: ConsultantDeadlineRowFilters): boolean {
  return activeRowFilterValues(rowFilters).length > 0;
}

/** Changes whenever any row filter changes, so a new filter choice is a new view. */
export function rowFiltersKey(rowFilters: ConsultantDeadlineRowFilters): string {
  return Object.values(rowFilters).join('|');
}

/** Narrowing filters only: search has its own field and the view is not a filter. */
export function countActiveFilters(filters: ConsultantDeadlinesUrlFilters): number {
  return (filters.tenantId ? 1 : 0) + activeRowFilterValues(toRowFilters(filters)).length;
}
